import { useChainNow } from '../../hooks/useChainClock.tsx';
import { useTimelockOps, type TimelockOp } from '../../hooks/useTimelockOps.ts';
import { useI18n } from '../../i18n/index.tsx';
import { shortAddr } from '../../lib/format.ts';
import { describeCall } from '../../lib/timelock.ts';
import { Card, Pill } from '../ui/primitives.tsx';

/** The operations still to run: waiting for their delay, or ready. */
export function pendingOps(ops: TimelockOp[]): TimelockOp[] {
  return ops.filter((o) => o.state === 1 || o.state === 2);
}

/**
 * Every change scheduled on the timelock, in plain words, with the moment it can run: the public notice the 48-hour
 * delay exists for. Shown on the Docs page to everyone.
 */
export function UpcomingChanges() {
  const { t } = useI18n();
  const { ops, isLoading } = useTimelockOps();
  const now = useChainNow(10_000);
  const pending = pendingOps(ops);
  return (
    <Card title={t('docs.upcomingH')}>
      <p className="soft" style={{ marginTop: 0 }}>
        {t('docs.upcomingNote')}
      </p>
      {pending.length === 0 ? (
        <p className="soft" style={{ margin: 0 }} data-testid="upcoming-none">
          {isLoading ? t('common.loading') : t('docs.upcomingNone')}
        </p>
      ) : (
        <ul className="upcoming" data-testid="upcoming">
          {pending.map((o) => {
            const ready = o.state === 2 || (o.timestamp > 1 && now >= o.timestamp);
            return (
              <li key={o.id} data-testid="upcoming-op">
                <div className="upcoming-when">
                  <Pill tone={ready ? 'green' : 'amber'}>{ready ? t('docs.upcomingReady') : t('docs.upcomingAt', { date: new Date(o.timestamp * 1000).toLocaleString() })}</Pill>
                  {o.predecessor !== `0x${'0'.repeat(64)}` ? <span className="muted num">{t('docs.upcomingAfter', { id: shortAddr(o.predecessor) })}</span> : null}
                </div>
                <ul className="upcoming-calls">
                  {o.calls.map((c, i) => (
                    <li key={i}>{describeCall(c.target, c.data, t)}</li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
